import { merge } from 'lodash';
import { useIntl } from 'react-intl';
import {
  EmptyComponent,
  EvidenceProps,
  abortableFetch,
  updateItemFactory,
} from '@eas/common-web';
import { PairedLockEditButton } from '@composite/detail/paired-lock-edit-button';
import { useAuthoredEvidence } from '@composite/evidence/authored-evidence';
import { SaveButton } from '@composite/settlement-method/save-button';
import {
  createLockedFileStyle,
  isEsFileDetailLocked,
} from '@components/evidence/utils/is-locked';
import { CourtFile, EsFile } from '@models';
import {
  Agenda,
  EntityName,
  EsFileState,
  EvidenceApiUrl,
  ExportTags,
} from '@enums';
import { ActionBar } from './es-file-actionbar';
import { useColumns } from './es-file-columns';
import { Fields } from './es-file-fields';
import { useEsFilesHookedSources } from './es-file-hooked-sources';
import { useValidationSchema } from './es-file-schema';
import { RemoveButton } from './es-file-toolbar';
import { disableBtnFactory } from './es-file-utils';

export const ES_FILES_IDENTIFIER = 'ES_FILES';
export const ES_FILES_VERSION = 14;

export function useEsFiles({
  evidenceProps,
}: {
  evidenceProps?: Partial<EvidenceProps<EsFile>>;
}) {
  const intl = useIntl();

  const defaultColumns = useColumns();
  const columns = evidenceProps?.tableProps?.columns ?? defaultColumns;

  const validationSchema = useValidationSchema();

  const props = merge(
    {
      version: ES_FILES_VERSION,
      identifier: ES_FILES_IDENTIFIER,
      apiProps: {
        url: EvidenceApiUrl.ES_FILES,
        hookedSources: useEsFilesHookedSources,
        updateItem: updateItemFactory({
          updateItemMethod: updateEsFile,
        }),
      },
      tableProps: {
        columns,
        tableName: intl.formatMessage({
          id: 'ES__ES_FILES__TABLE__TITLE',
          defaultMessage: 'Spisy',
        }),
        reportTag: ExportTags.ES_FILES,
        defaultSorts: [],
        createRowStyle: createLockedFileStyle,
        showBulkActionButton: false,
        showNamedSettingsButton: false,
      },
      detailProps: {
        entityName: EntityName.ES_FILE,
        FieldsComponent: Fields,
        GeneralFieldsComponent: EmptyComponent,
        lock: isEsFileDetailLocked,
        toolbarProps: {
          EditButton: PairedLockEditButton,
          title: intl.formatMessage({
            id: 'ES__ES_FILES__DETAIL__TITLE',
            defaultMessage: 'Spis',
          }),
          style: {
            background: 'linear-gradient(to bottom, #FAFAFA, #64B5F6)',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
            borderBottom: '3px solid #2196F3',
          },
          subtitle: (value: EsFile) => value?.number?.value ?? '',
          disableBtn: disableBtnFactory(),
          SaveButton,
          RemoveButton,
          ActionBar,
        },
        validationSchema,
      },
    },
    evidenceProps
  );

  const evidenceBag = useAuthoredEvidence<EsFile>(props);

  return { evidenceBag, props };
}

function updateEsFile(api: string, item: EsFile, initialItem: EsFile) {
  if (
    initialItem.agenda !== Agenda.COURT ||
    initialItem.state !== EsFileState.CLOSED
  ) {
    const requestItem = item as any;
    const { changeReason, ...updatedItem } = requestItem;
    return abortableFetch(
      `${api}/${item.id}${
        changeReason ? `?changeReason=${encodeURIComponent(changeReason)}` : ''
      }`,
      {
        headers: new Headers({ 'Content-Type': 'application/json' }),
        method: 'PUT',
        body: JSON.stringify(updatedItem),
      }
    );
  }

  const courtFile = item as CourtFile;

  return abortableFetch(`${api}/${item.id}/update-closed`, {
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    method: 'PUT',
    body: JSON.stringify({
      version: courtFile.version,
      processingType: courtFile.processingType,
      settlementMethods: courtFile.settlementMethods,
      proceedingsSubjects: courtFile.proceedingsSubjects,
      subjectIndexes: courtFile.subjectIndexes,
      analystUserRoleId: courtFile.analystUserRole
        ? { id: courtFile.analystUserRole.id }
        : undefined,
    }),
  });
}
